import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, authHeaders, formatApiErrorDetail } from "@/contexts/AuthContext";
import { useSiteContent, DEFAULT_CONTENT } from "@/contexts/SiteContentContext";
import AdminTopbar from "@/components/admin/AdminTopbar";
import {
  Save,
  RefreshCw,
  Trash2,
  Plus,
  RotateCcw,
  Loader2,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContent() {
  const { token } = useAuth();
  const { refresh: refreshPublicContent } = useSiteContent();

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Load current content
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site-content`);
        if (!active) return;
        // Ensure every key exists by merging with defaults
        setDraft({
          hero: { ...DEFAULT_CONTENT.hero, ...(data?.hero || {}) },
          services: {
            ...DEFAULT_CONTENT.services,
            ...(data?.services || {}),
            items:
              data?.services?.items?.length === DEFAULT_CONTENT.services.items.length
                ? data.services.items
                : DEFAULT_CONTENT.services.items,
          },
          worldwide: {
            ...DEFAULT_CONTENT.worldwide,
            ...(data?.worldwide || {}),
            cities: data?.worldwide?.cities || DEFAULT_CONTENT.worldwide.cities,
          },
          contact: { ...DEFAULT_CONTENT.contact, ...(data?.contact || {}) },
        });
      } catch {
        setDraft(DEFAULT_CONTENT);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const setField = (section, field, value) =>
    setDraft((d) => ({ ...d, [section]: { ...d[section], [field]: value } }));

  const setServiceItem = (idx, field, value) =>
    setDraft((d) => {
      const items = d.services.items.map((it, i) =>
        i === idx ? { ...it, [field]: value } : it
      );
      return { ...d, services: { ...d.services, items } };
    });

  const setCity = (idx, field, value) =>
    setDraft((d) => {
      const cities = d.worldwide.cities.map((c, i) =>
        i === idx ? { ...c, [field]: value } : c
      );
      return { ...d, worldwide: { ...d.worldwide, cities } };
    });

  const removeCity = (idx) =>
    setDraft((d) => ({
      ...d,
      worldwide: {
        ...d.worldwide,
        cities: d.worldwide.cities.filter((_, i) => i !== idx),
      },
    }));

  const addCity = () =>
    setDraft((d) => ({
      ...d,
      worldwide: {
        ...d.worldwide,
        cities: [
          ...d.worldwide.cities,
          { city: "", country: "", tag: "Affiliate", hot: false },
        ],
      },
    }));

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, draft, {
        headers: authHeaders(token),
      });
      await refreshPublicContent();
      toast.success("Content saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL content to defaults? This cannot be undone."))
      return;
    setResetting(true);
    try {
      await axios.post(
        `${API}/admin/site-content/reset`,
        {},
        { headers: authHeaders(token) }
      );
      const { data } = await axios.get(`${API}/site-content`);
      setDraft({
        hero: { ...DEFAULT_CONTENT.hero, ...(data?.hero || {}) },
        services: { ...DEFAULT_CONTENT.services, ...(data?.services || {}) },
        worldwide: { ...DEFAULT_CONTENT.worldwide, ...(data?.worldwide || {}) },
        contact: { ...DEFAULT_CONTENT.contact, ...(data?.contact || {}) },
      });
      await refreshPublicContent();
      toast.success("Content reset to defaults");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed"
      );
    } finally {
      setResetting(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="min-h-screen bg-[#F2EBDC] text-[#0A0A0A]">
        <AdminTopbar />
        <div className="flex items-center justify-center py-32 font-mono text-xs uppercase tracking-[0.25em]">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading content…
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-content-page"
      className="min-h-screen bg-[#F2EBDC] text-[#0A0A0A] pb-32"
    >
      <AdminTopbar />

      <main className="max-w-[1100px] mx-auto px-5 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2D9C] mb-2">
              ✶ Edit · Landing content
            </div>
            <h1
              className="font-display"
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
                lineHeight: 0.9,
              }}
            >
              Edit the page.
            </h1>
            <p className="font-mono text-xs text-[#0A0A0A]/70 mt-2 max-w-xl">
              Live texts of the landing. Save to publish. Big display titles
              and platform list are hard-coded and not editable here.
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View site
          </a>
        </div>

        {/* HERO */}
        <Section title="① Hero" id="cms-hero">
          <Field
            label="Deck (paragraph)"
            multiline
            value={draft.hero.deck}
            onChange={(v) => setField("hero", "deck", v)}
            testid="cms-hero-deck"
          />
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Stat 1 — value"
              value={draft.hero.stat_1_value}
              onChange={(v) => setField("hero", "stat_1_value", v)}
              testid="cms-hero-stat-1-value"
            />
            <Field
              label="Stat 1 — label"
              value={draft.hero.stat_1_label}
              onChange={(v) => setField("hero", "stat_1_label", v)}
              testid="cms-hero-stat-1-label"
            />
            <Field
              label="Stat 2 — value"
              value={draft.hero.stat_2_value}
              onChange={(v) => setField("hero", "stat_2_value", v)}
              testid="cms-hero-stat-2-value"
            />
            <Field
              label="Stat 2 — label"
              value={draft.hero.stat_2_label}
              onChange={(v) => setField("hero", "stat_2_label", v)}
              testid="cms-hero-stat-2-label"
            />
            <Field
              label="Stat 3 — value"
              value={draft.hero.stat_3_value}
              onChange={(v) => setField("hero", "stat_3_value", v)}
              testid="cms-hero-stat-3-value"
            />
            <Field
              label="Stat 3 — label"
              value={draft.hero.stat_3_label}
              onChange={(v) => setField("hero", "stat_3_label", v)}
              testid="cms-hero-stat-3-label"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Polaroid 1 caption"
              value={draft.hero.polaroid_1_caption}
              onChange={(v) => setField("hero", "polaroid_1_caption", v)}
            />
            <Field
              label="Polaroid 2 caption"
              value={draft.hero.polaroid_2_caption}
              onChange={(v) => setField("hero", "polaroid_2_caption", v)}
            />
            <Field
              label="Starburst year"
              value={draft.hero.starburst_year}
              onChange={(v) => setField("hero", "starburst_year", v)}
            />
            <Field
              label="Handwritten note"
              value={draft.hero.handwritten_note}
              onChange={(v) => setField("hero", "handwritten_note", v)}
            />
          </div>
        </Section>

        {/* SERVICES */}
        <Section title="③ Services" id="cms-services">
          <Field
            label="Intro paragraph"
            multiline
            value={draft.services.intro}
            onChange={(v) => setField("services", "intro", v)}
            testid="cms-services-intro"
          />
          <div className="space-y-4">
            {draft.services.items.map((s, i) => (
              <div
                key={i}
                className="border-2 border-[#0A0A0A]/15 rounded-xl p-4 bg-[#FAF5E8]"
                data-testid={`cms-service-${i + 1}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2D9C] mb-3">
                  Service 0{i + 1}
                </div>
                <Field
                  label="Title"
                  value={s.title}
                  onChange={(v) => setServiceItem(i, "title", v)}
                />
                <Field
                  label="Body"
                  multiline
                  value={s.body}
                  onChange={(v) => setServiceItem(i, "body", v)}
                />
                <Field
                  label="Handwritten note"
                  value={s.note}
                  onChange={(v) => setServiceItem(i, "note", v)}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* WORLDWIDE */}
        <Section title="④ Worldwide" id="cms-worldwide">
          <Field
            label="Body (paragraph)"
            multiline
            value={draft.worldwide.body}
            onChange={(v) => setField("worldwide", "body", v)}
            testid="cms-worldwide-body"
          />
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Stamp year"
              value={draft.worldwide.stamp_year}
              onChange={(v) => setField("worldwide", "stamp_year", v)}
            />
            <Field
              label="Spec — Office"
              value={draft.worldwide.spec_office}
              onChange={(v) => setField("worldwide", "spec_office", v)}
            />
            <Field
              label="Spec — Markets"
              value={draft.worldwide.spec_markets}
              onChange={(v) => setField("worldwide", "spec_markets", v)}
            />
            <Field
              label="Spec — Coverage"
              value={draft.worldwide.spec_coverage}
              onChange={(v) => setField("worldwide", "spec_coverage", v)}
            />
            <Field
              label="Spec — Travel"
              value={draft.worldwide.spec_travel}
              onChange={(v) => setField("worldwide", "spec_travel", v)}
            />
          </div>

          {/* Cities */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2D9C]">
                Cities ({draft.worldwide.cities.length})
              </div>
              <button
                type="button"
                onClick={addCity}
                data-testid="cms-city-add"
                className="inline-flex items-center gap-2 bg-[#FFD500] text-[#0A0A0A] px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FAF5E8] transition-colors"
              >
                <Plus className="w-3 h-3" /> Add city
              </button>
            </div>
            <div className="space-y-3">
              {draft.worldwide.cities.map((c, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 items-center bg-[#FAF5E8] border-2 border-[#0A0A0A]/15 rounded-xl p-3"
                  data-testid={`cms-city-${i}`}
                >
                  <input
                    type="text"
                    value={c.city}
                    onChange={(e) => setCity(i, "city", e.target.value)}
                    placeholder="City"
                    className="col-span-4 input-zine"
                    style={{ padding: "8px 0" }}
                    data-testid={`cms-city-${i}-name`}
                  />
                  <input
                    type="text"
                    value={c.country}
                    onChange={(e) => setCity(i, "country", e.target.value)}
                    placeholder="Country"
                    className="col-span-3 input-zine"
                    style={{ padding: "8px 0" }}
                  />
                  <input
                    type="text"
                    value={c.tag}
                    onChange={(e) => setCity(i, "tag", e.target.value)}
                    placeholder="Tag (HQ, Affiliate…)"
                    className="col-span-3 input-zine"
                    style={{ padding: "8px 0" }}
                  />
                  <label className="col-span-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em]">
                    <input
                      type="checkbox"
                      checked={!!c.hot}
                      onChange={(e) => setCity(i, "hot", e.target.checked)}
                      className="accent-[#FF2D9C]"
                    />
                    HQ
                  </label>
                  <button
                    type="button"
                    onClick={() => removeCity(i)}
                    aria-label="Remove city"
                    data-testid={`cms-city-${i}-remove`}
                    className="col-span-1 w-8 h-8 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#FF2D9C] hover:border-[#FF2D9C] hover:text-[#FAF5E8] transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* CONTACT */}
        <Section title="⑤ Contact" id="cms-contact">
          <Field
            label="Intro paragraph"
            multiline
            value={draft.contact.intro}
            onChange={(v) => setField("contact", "intro", v)}
            testid="cms-contact-intro"
          />
          <Field
            label="Public email"
            value={draft.contact.email}
            onChange={(v) => setField("contact", "email", v)}
            testid="cms-contact-email"
          />
          <Field
            label="Address line 1"
            value={draft.contact.address_line_1}
            onChange={(v) => setField("contact", "address_line_1", v)}
          />
          <Field
            label="Address line 2"
            value={draft.contact.address_line_2}
            onChange={(v) => setField("contact", "address_line_2", v)}
          />
        </Section>
      </main>

      {/* Sticky save bar */}
      <div
        data-testid="cms-save-bar"
        className="fixed bottom-0 left-0 right-0 z-30 bg-[#0A0A0A] text-[#FAF5E8] border-t-2 border-[#FFD500]"
      >
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FAF5E8]/70">
            ✶ Edits live only after Save.
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={resetting}
              data-testid="cms-reset"
              className="inline-flex items-center gap-2 bg-transparent text-[#FAF5E8] px-4 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] border-2 border-[#FAF5E8]/40 hover:border-[#FAF5E8] disabled:opacity-60"
            >
              {resetting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RotateCcw className="w-3.5 h-3.5" />
              )}
              Reset all
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="cms-save"
              className="inline-flex items-center gap-2 bg-[#FFD500] text-[#0A0A0A] px-5 py-2.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] border-2 border-[#FFD500] hover:bg-[#FAF5E8] hover:border-[#FAF5E8] disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5" />
                  Save changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, id, children }) {
  return (
    <section
      id={id}
      className="mb-10 bg-[#F2EBDC] border-2 border-[#0A0A0A] rounded-2xl p-6 lg:p-8"
      style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
    >
      <h2 className="font-display text-3xl mb-5">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, multiline, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70">
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="input-zine resize-y"
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-zine"
          data-testid={testid}
        />
      )}
    </div>
  );
}
